import { observer } from 'mobx-react';
import React, { useContext, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { useAccount, useWalletClient } from 'wagmi';
import folderIcon from '../../../assets/icons/folder.png';
import { useBreakpointContext } from '../../../context-providers/BreakpointProvider';
import { ModalContext } from '../../../context-providers/ModalProvider';
import { ServicesContext } from '../../../context-providers/ServicesProvider';
import { getPositionNameWithColor } from '../../../helpers/formatters';
import { positionsStore } from '../../../services/position/position.store';
import EmptyStateComponent from '../../EmptyState/EmptyState';
import ClosePosition from '../../modals/ClosePosition/ClosePosition';
import { getDataGridCommonProps } from '../helpers/data-grid-common-props';
import './UserPositions.scss';

import { getOpenPositionsColumns } from '../helpers/position-columns';
import PositionSmallTableRow from '../small-table-rows/PositionSmallTableRow';
import EditPosition, { TpSlParams, TpSlPositionParams } from "../../modals/EditPosition/EditPosition";
import { utils } from "ethers";
import { IPositionResponse } from "../../../services/api/api.interfaces";

const PAGE_SIZE = 100;
const emptyStateText = 'No active positions yet';

const UserPositions = observer(() => {

    const { address } = useAccount();
    const { data: signer } = useWalletClient();
    const { openModal } = useContext(ModalContext);
    const { positionsManager } = useContext(ServicesContext);
    const { xs } = useBreakpointContext();


    const [paginationEnabled, setPaginationEnabled] = React.useState(positionsStore.openPositionsList.length > PAGE_SIZE);

    useEffect(() => {
        setPaginationEnabled(positionsStore.openPositionsList.length > PAGE_SIZE);
    }, [positionsStore.openPositionsList.length])

    const closePosition = (positionId: number) => {
        if (address && signer) {
            const position = positionsStore.openPositionsList.find(position => position.id === positionId);
            if (!position) {
                return;
            }
            openModal(<ClosePosition position={position}/>);
        }
    };

    const changePosition = async (positionId: number, tpSlParams: TpSlParams) => {
        if (!address || !signer) {
            return;
        }

        const positionFromStore = positionsStore.openPositionsList.find(position => position.id === positionId);

        let tp = tpSlParams.tp ? utils.parseUnits(tpSlParams.tp + '', 8).toString() : '0';
        let sl = tpSlParams.sl ? utils.parseUnits(tpSlParams.sl + '', 8).toString() : '0';

        if (positionFromStore) {
            if (tpSlParams.tp === Number(positionFromStore.tpPrice)) {
                tp = '0';
            }
            if (tpSlParams.sl === Number(positionFromStore.slPrice)) {
                sl = '0';
            }
        }

        if (tp === '0' && sl === '0') {
            return;
        }

        await positionsManager.updatePosition({
            user: address,
            positionId,
            tp,
            sl
        });

    };

    const editPosition = (position: TpSlPositionParams & IPositionResponse) => {
        openModal(
            <EditPosition title={(
                <h2 className="nereus-body-m">Edit Position - {getPositionNameWithColor(position)}
                </h2>
            )} isEdit={true} applyChanges={(params) => changePosition(position.id, params)} position={position}/>
        );
    }

    return (<div className="TradeTabsNavigation__gridWrapper">
        {xs && positionsStore.openPositionsList?.length ? positionsStore.openPositionsList.map((position, index) => {
            return <PositionSmallTableRow key={index} position={position} closePosition={closePosition}
                                          editPosition={editPosition} openModal={openModal}/>;
        }) : null }
        {xs && !positionsStore.openPositionsList?.length ?
            <div className="TradeTabsNavigation__xsEmpty card-container"><EmptyStateComponent text={emptyStateText} icon={<img src={folderIcon} alt={emptyStateText}/>} /></div> : null
        }
        {!xs ?
            <DataGrid
                {...getDataGridCommonProps(emptyStateText)}

                rows={positionsStore.openPositionsList}
                columns={getOpenPositionsColumns({
                    editPosition, closePosition
                })}
                hideFooter={!paginationEnabled}
                initialState={{
                    pagination: {
                        paginationModel: { page: 0, pageSize: PAGE_SIZE }
                    }
                }}
            /> : null}
    </div>);
});

export default UserPositions;
